import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { CapacitorHttp, HttpOptions, HttpResponse as CapHttpResponse } from '@capacitor/core';

import { PlatformService } from '../auth/services/platform.service';
import { AuthService } from '../auth/services/auth.service';
import { APP_CONFIG } from '../config/app-config.token';
import { AppConfig } from '../config/app-config.model';

interface RequestOptions {
  headers?: HttpHeaders | { [header: string]: string | string[] };
  params?: HttpParams | { [param: string]: string | string[] };
}

// CapacitorHttp needs a plain string dict; callers may hand us an HttpHeaders
// instance (notably the saviscio bypass path, which skips the interceptor logic
// that used to normalize them).
function toHeaderDict(headers?: HttpHeaders | { [header: string]: string | string[] }): { [key: string]: string } {
  if (!headers) { return {}; }
  const dict: { [key: string]: string } = {};
  if (headers instanceof HttpHeaders) {
    for (const key of headers.keys()) {
      const values = headers.getAll(key);
      if (values) { dict[key] = values.join(', '); }
    }
    return dict;
  }
  for (const [key, value] of Object.entries(headers)) {
    dict[key] = Array.isArray(value) ? value.join(', ') : value;
  }
  return dict;
}

/**
 * CapacitorHttpService
 *
 * Platform-aware HTTP service that:
 * - Web: Uses Angular HttpClient (goes through interceptors, subject to CORS)
 * - Mobile: Uses CapacitorHttp (native requests, bypasses CORS)
 *
 * This service replicates the interceptor logic for mobile platforms,
 * applying security validations, URL rewriting, and header injection.
 */
@Injectable({
  providedIn: 'root'
})
export class CapacitorHttpService {
  private http = inject(HttpClient);
  private platformService = inject(PlatformService);
  private authService = inject(AuthService);
  private config = inject<AppConfig>(APP_CONFIG);


  /**
   * GET request
   */
  get<T>(url: string, options?: RequestOptions): Observable<T> {
    if (this.platformService.isMobile()) {
      return this.mobileGet<T>(url, options);
    } else {
      return this.http.get<T>(url, options);
    }
  }

  /**
   * POST request
   */
  post<T>(url: string, body: unknown, options?: RequestOptions): Observable<T> {
    if (this.platformService.isMobile()) {
      return this.mobilePost<T>(url, body, options);
    } else {
      return this.http.post<T>(url, body, options);
    }
  }

  /**
   * PUT request
   */
  put<T>(url: string, body: unknown, options?: RequestOptions): Observable<T> {
    if (this.platformService.isMobile()) {
      return this.mobilePut<T>(url, body, options);
    } else {
      return this.http.put<T>(url, body, options);
    }
  }

  /**
   * DELETE request
   */
  delete<T>(url: string, options?: RequestOptions): Observable<T> {
    if (this.platformService.isMobile()) {
      return this.mobileDelete<T>(url, options);
    } else {
      return this.http.delete<T>(url, options);
    }
  }

  /**
   * PATCH request
   */
  patch<T>(url: string, body: unknown, options?: RequestOptions): Observable<T> {
    if (this.platformService.isMobile()) {
      return this.mobilePatch<T>(url, body, options);
    } else {
      return this.http.patch<T>(url, body, options);
    }
  }

  // ========== MOBILE IMPLEMENTATIONS ==========

  private mobileGet<T>(url: string, options?: RequestOptions): Observable<T> {
    return from(this.executeMobileRequest<T>('GET', url, null, options));
  }

  private mobilePost<T>(url: string, body: unknown, options?: RequestOptions): Observable<T> {
    return from(this.executeMobileRequest<T>('POST', url, body, options));
  }

  private mobilePut<T>(url: string, body: unknown, options?: RequestOptions): Observable<T> {
    return from(this.executeMobileRequest<T>('PUT', url, body, options));
  }

  private mobileDelete<T>(url: string, options?: RequestOptions): Observable<T> {
    return from(this.executeMobileRequest<T>('DELETE', url, null, options));
  }

  private mobilePatch<T>(url: string, body: unknown, options?: RequestOptions): Observable<T> {
    return from(this.executeMobileRequest<T>('PATCH', url, body, options));
  }

  /**
   * Execute mobile HTTP request using CapacitorHttp
   * Applies auth header logic similar to AuthHeaderInterceptor
   */
  private async executeMobileRequest<T>(
    method: string,
    url: string,
    body: unknown,
    options?: RequestOptions
  ): Promise<T> {
    // Bypass Saviscio requests (same as interceptor)
    if (url.includes('saviscio')) {
      return this.nativeHttpRequest<T>(method, url, body, options);
    }

    // Apply interceptor logic
    const processedRequest = this.applyInterceptorLogic(url, options);

    if (!processedRequest.allowed) {
      throw new Error(processedRequest.error || 'Request blocked by security validation');
    }

    // Execute native HTTP request
    return this.nativeHttpRequest<T>(
      method,
      processedRequest.url!,
      body,
      processedRequest.options
    );
  }

  /**
   * Apply AuthHeaderInterceptor logic for mobile
   * Returns processed URL and options, or error if blocked
   */
  private applyInterceptorLogic(url: string, options?: RequestOptions): {
    allowed: boolean;
    url?: string;
    options?: RequestOptions;
    error?: string;
  } {
    const isInternalUser = this.authService.isInternalUser();
    const inss = this.authService.getInss();

    // Prepare headers
    let headers: { [key: string]: string } = {};

    // Copy existing headers
    if (options?.headers) {
      if (options.headers instanceof HttpHeaders) {
        const httpHeaders = options.headers;
        httpHeaders.keys().forEach((key: string) => {
          headers[key] = httpHeaders.get(key) || '';
        });
      } else {
        const plainHeaders: { [key: string]: string } = {};
        Object.assign(plainHeaders, options.headers);
        headers = plainHeaders;
      }
    }

    // Add Authorization header
    const token = this.authService.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // SECURITY: Handle /me URL rewriting based on user type
    let processedUrl = url;

    // NEW SECURITY MODEL:
    // - External users: Send /me to Tyk, which rewrites to /{jwt.preferred_username}
    // - Internal users: Send /{inss} directly (for search capability)

    if (!isInternalUser && inss) {
      // Validate INSS format
      if (!this.validateInssFormat(inss)) {
        console.error(
          `[CapacitorHttpService] Invalid INSS format: "${inss}" ` +
          '(expected 11 digits with valid date components). Rejecting request.'
        );

        this.logSecurityEvent('invalid_inss_format', {
          inss: this.maskInss(inss),
          url: url,
          method: 'REQUEST',
          timestamp: new Date().toISOString()
        });

        return {
          allowed: false,
          error: 'Invalid INSS format - request rejected'
        };
      }

      // IMPORTANT: Do NOT rewrite /me for external users!
      // Tyk handles the /me → /{jwt.preferred_username} rewrite on the server side.
      // External users are jailed to /me endpoints (security model).

      // Only add X-Ssin header (for Tyk to validate against JWT)
      headers['X-Ssin'] = inss;

      // Keep URL as-is (don't rewrite /me)
      processedUrl = url;
    } else if (isInternalUser) {
      // Internal users can use /{inss} endpoints directly (search capability)
      // URL is already correct, no rewriting needed
      processedUrl = url;
    }

    return {
      allowed: true,
      url: processedUrl,
      options: {
        ...options,
        headers
      }
    };
  }

  /**
   * Execute native HTTP request using CapacitorHttp
   */
  private async nativeHttpRequest<T>(
    method: string,
    url: string,
    body: unknown,
    options?: RequestOptions
  ): Promise<T> {
    const httpOptions: HttpOptions = {
      url: url,
      method: method,
      headers: toHeaderDict(options?.headers),
    };

    // Add body for POST/PUT/PATCH
    if (body && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      httpOptions.data = body;
      // Ensure Content-Type is set
      if (!httpOptions.headers!['Content-Type']) {
        httpOptions.headers!['Content-Type'] = 'application/json';
      }
    }

    // Add query params if present
    if (options?.params) {
      const params: { [key: string]: string } = {};
      if (options.params instanceof HttpParams) {
        const httpParams = options.params;
        httpParams.keys().forEach((key: string) => {
          params[key] = httpParams.get(key) || '';
        });
      } else {
        Object.assign(params, options.params);
      }
      httpOptions.params = params;
    }

    try {
      let response: CapHttpResponse;

      switch (method.toUpperCase()) {
        case 'GET':
          response = await CapacitorHttp.get(httpOptions);
          break;
        case 'POST':
          response = await CapacitorHttp.post(httpOptions);
          break;
        case 'PUT':
          response = await CapacitorHttp.put(httpOptions);
          break;
        case 'DELETE':
          response = await CapacitorHttp.delete(httpOptions);
          break;
        case 'PATCH':
          response = await CapacitorHttp.patch(httpOptions);
          break;
        default:
          response = await CapacitorHttp.request(httpOptions);
      }

      if (response.status >= 200 && response.status < 300) {
        return response.data as T;
      } else {
        console.error('[CapacitorHttpService] HTTP Error Response:', {
          status: response.status,
          statusText: response.status,
          url: response.url,
          data: response.data,
          headers: response.headers
        });
        throw new Error(`HTTP ${response.status}: ${JSON.stringify(response.data)}`);
      }
    } catch (error) {
      console.error('[CapacitorHttpService] Native HTTP error:', error);
      throw error;
    }
  }

  // ========== SECURITY VALIDATION METHODS (mobile-specific) ==========

  /**
   * Validate Belgian INSS format (11 digits with date components)
   *
   * Format: YYMMDD-XXX-CD
   * - YY: Birth year (2 digits)
   * - MM: Birth month (01-12, +20 for 2000+, +40 for unknown month)
   * - DD: Birth day (01-31, +40 for unknown day)
   * - XXX: Serial number (odd=male, even=female)
   * - CD: Check digit (mod 97 validation)
   *
   * Only the 11-digit shape is enforced. That is the guard that matters here:
   * this value is interpolated into a request URL, so it must be numeric and of
   * known length. Everything derived from it — date components, check digit —
   * is reported and allowed through, because the backend is the authority on
   * whether an INSS identifies a member, and this wrapper is not.
   *
   * Date components used to reject. They cannot: an INSS is an identifier, not
   * a date. The AS400 issues synthetic identities whose digits do not decode to
   * a real birth date, and member data genuinely lives behind them. Rejecting
   * here meant the request never left the device, so the app hung on a spinner
   * while curl against the same backend worked — the wrapper was the only thing
   * refusing. See the test-account NISS 77017777888: day digits "77" give a
   * base day of 37 once the +40 unknown-day convention is applied.
   */
  private validateInssFormat(inss: string): boolean {
    // Must be exactly 11 digits — the one hard requirement.
    const basicFormat = /^[0-9]{11}$/;
    if (!basicFormat.test(inss)) {
      return false;
    }

    // Extract date components (the year digits carry no validatable range)
    const month = parseInt(inss.substring(2, 4), 10);
    const day = parseInt(inss.substring(4, 6), 10);

    // Month (01-12, or +20/+40 for special cases) and day (01-31, or +40 for
    // unknown day). Reported only — see the note above.
    const baseMonth = month > 40 ? month - 40 : month > 20 ? month - 20 : month;
    const baseDay = day > 40 ? day - 40 : day;

    const dateIssues: string[] = [];
    if (baseMonth < 1 || baseMonth > 12) {
      dateIssues.push(`month ${baseMonth} outside 1-12`);
    }
    if (baseDay < 1 || baseDay > 31) {
      dateIssues.push(`day ${baseDay} outside 1-31`);
    }
    if (baseMonth === 2 && baseDay > 29) {
      dateIssues.push(`day ${baseDay} in February`);
    }
    if ([4, 6, 9, 11].includes(baseMonth) && baseDay > 30) {
      dateIssues.push(`day ${baseDay} in a 30-day month`);
    }

    if (dateIssues.length > 0) {
      console.warn(
        '[CapacitorHttpService] INSS date component warning: ' +
        `${dateIssues.join(', ')}. Allowing request — the backend validates authoritatively.`
      );
      this.logSecurityEvent('inss_date_component_warning', {
        inss: this.maskInss(inss),
        issues: dateIssues,
        timestamp: new Date().toISOString()
      });
    }

    // Check digit validation (mod 97)
    const mainDigits = inss.substring(0, 9);
    const checkDigits = inss.substring(9, 11);
    const calculated1900 = 97 - (parseInt(mainDigits, 10) % 97);
    const calculated2000 = 97 - (parseInt('2' + mainDigits, 10) % 97);
    const checkValue = parseInt(checkDigits, 10);

    if (checkValue !== calculated1900 && checkValue !== calculated2000) {
      console.warn(
        '[CapacitorHttpService] INSS check digit validation warning: ' +
        `expected ${calculated1900} (1900s) or ${calculated2000} (2000s), got ${checkValue}`
      );
      // Log but don't reject - backend will do authoritative validation
      this.logSecurityEvent('inss_check_digit_warning', {
        inss: this.maskInss(inss),
        expected_1900: calculated1900,
        expected_2000: calculated2000,
        actual: checkValue,
        timestamp: new Date().toISOString()
      });
    }

    return true;
  }

  /**
   * Mask INSS for logging (security)
   */
  private maskInss(inss: string): string {
    if (!inss || inss.length < 4) {
      return '***';
    }
    return inss.substring(0, 2) + '***' + inss.substring(inss.length - 2);
  }

  /**
   * Log security event (for monitoring)
   */
  private logSecurityEvent(eventType: string, details: Record<string, unknown>): void {
    if (this.config.production) {
      // In production, send to monitoring service (Instana, etc.)
      console.warn(`[SECURITY EVENT] ${eventType}`, details);
      // TODO: Send to monitoring service
    } else {
      // In development, detailed console logging
      console.warn(`[SECURITY EVENT] ${eventType}`, details);
    }
  }
}
